import { TextTypeEnum } from './custom-template';

/**
 * КОМУ ПРИНАДЛЕЖИТ ТЕКСТ ВНУТРИ ПЕРСОННОЙ ГРУППЫ — один закон на обе половины пары «заполнить/снять».
 *
 * Живёт здесь, а не рядом с гидрацией, по одной причине: закон нужен ДВУМ пакетам, которые друг
 * о друге не знают. Заполнение (`resolvePersonTextsPositional`, dto-fotoclass-book) спрашивает
 * «что положить», освобождение (`releaseRosterCell`, photobook-model) — «что стереть». Оба обязаны
 * отвечать про один и тот же бокс одинаково; разойдись они, освобождение стёрло бы то, чего
 * заполнение никогда не писало, и вернуть это было бы нечем. `photobook-model` от
 * `dto-fotoclass-book` не зависит, зато оба зависят отсюда — поэтому закон лежит рядом с
 * `TextTypeEnum`, который его и определяет.
 */

/** Текст, который подставляется ИЗ КАРТОЧКИ ПЕРСОНЫ и на бумаге печатает её данные. */
export const PERSON_TEXT_TYPES: ReadonlySet<string> = new Set<string>([
  TextTypeEnum.PERSON_FIRST_NAME,
  TextTypeEnum.PERSON_LAST_NAME,
  TextTypeEnum.PERSON_FULL_NAME,
  TextTypeEnum.PERSON_PATRONYMIC,
  TextTypeEnum.PERSON_GIVEN_PATRONYMIC,
  TextTypeEnum.PERSON_QUOTE,
  TextTypeEnum.PERSON_SUBJECT,
]);

/**
 * What an UNMARKED box can end up printing: a group the designer never marked is filled positionally
 * from `getPersonTexts` (фамилия / имя-отчество / предмет), and the quote takes no part in that.
 */
export const POSITIONAL_PERSON_TEXT_TYPES: ReadonlySet<string> = new Set<string>(
  [...PERSON_TEXT_TYPES].filter(textType => textType !== TextTypeEnum.PERSON_QUOTE),
);

export function isPersonTextField(textType: string | undefined): boolean {
  return !!textType && PERSON_TEXT_TYPES.has(textType);
}

/**
 * «Дизайнер РАЗМЕТИЛ группу» — единственный ответ на этот вопрос для всех, кто его задаёт.
 *
 * Цитата разметкой НЕ считается: она в позиционном заполнении не участвует (`getPersonTexts` её не
 * отдаёт), и одна рамка цитаты в неразмеченной PSD-ячейке не должна выключать подстановку фамилии и
 * имени — а выключала бы, считайся она разметкой (ac5.1).
 */
export function isPersonMarkupField(textType: string | undefined): boolean {
  return !!textType && POSITIONAL_PERSON_TEXT_TYPES.has(textType);
}

/** Размеченный НЕ персональный текст (школа, год выпуска, заголовки виньетки) — принадлежит шаблону, не персоне. */
export function isForeignToPersonText(textType: string | undefined): boolean {
  return !!textType && textType !== TextTypeEnum.FREE_TEXT && !PERSON_TEXT_TYPES.has(textType);
}

/**
 * ЕДИНСТВЕННЫЙ ответ на «этот бокс принадлежит персоне ячейки?». Заполнение пишет ровно туда, где
 * `true`; освобождение стирает ровно там, где `true`. Симметрия — не стилистика: у бокса, который
 * заполнение никогда не трогало, второго источника может не быть вовсе.
 *
 * `groupHasPersonMarkup` — «дизайнер разметил группу» по `isPersonMarkupField`: в ней есть ХОТЬ ОДИН
 * текст позиционного персонального типа (цитата разметкой не считается). Без разметки безымянные
 * боксы заполняются позиционно (фамилия/имя), и тогда они персоне принадлежат; с разметкой
 * безымянный бокс — свободный текст АЛЬБОМА, и его не трогают.
 *
 * `freeText` сюда не попадает никогда, кроме неразмеченной группы: `TextScope.ALBUM` (текст
 * принадлежит альбому и живёт только в модели книги), восстановителя у него нет по построению —
 * `applySettingsToModel` его исключает явно.
 */
export function personOwnsGroupText(textType: string | undefined, groupHasPersonMarkup: boolean): boolean {
  if (isPersonTextField(textType)) return true;
  if (isForeignToPersonText(textType)) return false;

  return !groupHasPersonMarkup;
}
